/**
 * TipTap extension: Paste & Drop Image Upload
 *
 * Intercepts pasted or dropped image files, uploads them to the server,
 * and inserts the resulting URL as a resizable image node.
 *
 * While uploading, a base64 placeholder is shown with an overlay spinner.
 * Once the upload succeeds, the placeholder is replaced with the real URL.
 * On failure the placeholder is removed and a console warning is logged.
 */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { capLimitBytes, getUploadLimits } from '@ai-content-hub/shared-config';
import { measureImageWidth, uploadFile } from './tiptap-upload';

const IMAGE_MIME_RE = /^image\/(png|jpe?g|gif|webp|svg\+xml|bmp|tiff?)$/i;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB

// EAA-1212: read at paste/drop time (not module load) so a limit the app publishes
// after boot applies; never more than the server's configured media cap.
function maxImageSize(): number {
    return capLimitBytes(MAX_IMAGE_SIZE, getUploadLimits().mediaMaxSizeMb);
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

/** Read a File as a data-URL (base64) for the placeholder. */
function readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

/** Extract image files from a DataTransfer object. */
function getImageFiles(dataTransfer: DataTransfer): File[] {
    const files: File[] = [];
    for (let i = 0; i < dataTransfer.files.length; i++) {
        const file = dataTransfer.files[i];
        if (IMAGE_MIME_RE.test(file.type) && file.size <= maxImageSize()) {
            files.push(file);
        }
    }
    return files;
}

/* ------------------------------------------------------------------ */
/* Upload an image and insert / replace in the document                 */
/* ------------------------------------------------------------------ */

async function handleImageUpload(
    view: EditorView,
    file: File,
    insertPos: number
): Promise<void> {
    // 1. Read file as base64 placeholder
    let placeholder: string;
    try {
        placeholder = await readAsDataUrl(file);
    } catch {
        return; // can't read — bail silently
    }

    // 2. Insert placeholder image at the given position
    const imageNodeType =
        view.state.schema.nodes.image ?? view.state.schema.nodes.resizableImage;
    if (!imageNodeType) return;

    // EAA-1736: read the image's intrinsic width from the local data-URL and stamp
    // it on the node so the upload is inserted at its own size, not stretched to the
    // full editor width. The width is preserved when the placeholder src is later
    // swapped for the uploaded URL (see the `...node.attrs` spread below).
    // Measure BEFORE grabbing the transaction so `tr` is not stale after the await.
    const width = await measureImageWidth(placeholder);

    const placeholderAttrs = {
        src: placeholder,
        alt: file.name || 'Uploading…',
        title: 'Uploading…',
        ...(width != null ? { width } : {}),
    };

    const placeholderNode = imageNodeType.create(placeholderAttrs);
    view.dispatch(view.state.tr.insert(insertPos, placeholderNode));

    // 3. Upload file to the server
    const result = await uploadFile(file);

    // 4. Find and replace the placeholder node
    //    We iterate the current document to find the node whose src is the placeholder.
    let found = false;
    view.state.doc.descendants((node, pos) => {
        if (found) return false;
        if (
            node.type === imageNodeType &&
            (node.attrs as { src?: string }).src === placeholder
        ) {
            found = true;
            const newTr = view.state.tr;
            if (result.url) {
                // Replace placeholder with uploaded URL
                newTr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    src: result.url,
                    alt: file.name || '',
                    title: undefined,
                });
            } else {
                // Upload failed — remove the placeholder
                newTr.delete(pos, pos + node.nodeSize);
                console.warn('[RichText] Image upload failed:', result.error);
            }
            view.dispatch(newTr);
            return false;
        }
        return true;
    });
}

/* ------------------------------------------------------------------ */
/* Extension                                                            */
/* ------------------------------------------------------------------ */

const imagePasteDropPluginKey = new PluginKey('imagePasteDrop');

export const ImagePasteDrop = Extension.create({
    name: 'imagePasteDrop',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: imagePasteDropPluginKey,

                props: {
                    /* ---- Paste handler ---- */
                    handlePaste(view, event) {
                        const clipboardData = event.clipboardData;
                        if (!clipboardData) return false;

                        const imageFiles = getImageFiles(clipboardData);
                        if (imageFiles.length === 0) return false;

                        // Prevent default paste behaviour (e.g. base64 inline)
                        event.preventDefault();

                        const insertPos = view.state.selection.from;
                        imageFiles.forEach((file) => {
                            handleImageUpload(view, file, insertPos);
                        });

                        return true;
                    },

                    /* ---- Drop handler ---- */
                    handleDrop(view, event) {
                        const dataTransfer = (event as DragEvent).dataTransfer;
                        if (!dataTransfer) return false;

                        const imageFiles = getImageFiles(dataTransfer);
                        if (imageFiles.length === 0) return false;

                        event.preventDefault();

                        // Resolve the drop position
                        const coordinates = view.posAtCoords({
                            left: (event as DragEvent).clientX,
                            top: (event as DragEvent).clientY,
                        });
                        const insertPos = coordinates?.pos ?? view.state.selection.from;

                        imageFiles.forEach((file) => {
                            handleImageUpload(view, file, insertPos);
                        });

                        return true;
                    },
                },
            }),
        ];
    },
});

export default ImagePasteDrop;
